import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map } from 'rxjs';

import { VoteType } from '@ninety/platform/models/_shared/vote-type';
import { SortDirection } from '@ninety/platform/models/enums/sort-direction';
import { Sorted } from '@ninety/platform/models/enums/sorted';
import type { Issue } from '@ninety/platform/models/issues/issue';
import { IssueMessageType } from '@ninety/platform/models/issues/issue-message-type';
import { RocksIssuesEvents, RealTimeActions, TeamSelectors } from '@ninety/platform/state';

import { IssuesActions, IssuesBroadcastsActions } from './issues.actions';
import * as IssuesSelectors from './issues.selectors';

@Injectable()
export class IssuesBroadcastsEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);

  // Dispatch broadcast action for merged issue (doesn't trigger reducer, only broadcast)
  broadcastMergedIssueUpdate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.mergeIssuesSuccess),
      map(({ mergedIssue }) => {
        // Extract the changes that were made during merge
        const changes = {
          title: mergedIssue.title,
          description: mergedIssue.description ?? '',
          comments: mergedIssue.comments ?? [],
          attachments: mergedIssue.attachments ?? [],
        };
        return IssuesBroadcastsActions.broadcastMergedIssueUpdate({ issue: mergedIssue, changes });
      })
    )
  );

  //remap mergeIssuesSuccess to broadcastCompletedIssueFromMerge for broadcasting
  broadcastCompletedIssueFromMerge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.mergeIssuesSuccess),
      map(({ completedIssue }) => {
        return IssuesBroadcastsActions.broadcastCompletedIssueFromMerge({ issue: completedIssue });
      })
    )
  );

  // Broadcast effect for issue creation
  broadcastIssueCreated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.createIssueSuccess, IssuesActions.saveInlineSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.new,
            document: issue,
          },
        });
      })
    )
  );

  broadcastIssueUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateIssueSuccess, IssuesBroadcastsActions.broadcastMergedIssueUpdate),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue, changes }, channelId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: { ...changes, _id: issue._id },
          },
        })
      )
    )
  );

  broadcastIssueUserUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateUserSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue, user }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: {
              userId: user._id,
              //send the user over in case it is missing on the other side
              user: {
                _id: user._id,
                active: user.active,
                metadata: {
                  name: user.metadata?.name,
                  picture: user.metadata?.picture,
                },
              },
              _id: issue._id,
            },
          },
        });
      })
    )
  );

  broadcastIssueTeamUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateTeamSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId, teamId }, channelId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: { teamId, _id: issueId },
          },
        })
      )
    )
  );

  broadcastIssueIntervalChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setIssueIntervalSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([, channelId]) => !!channelId),
      map(([{ issue, intervalCode }, channelId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: { _id: issue._id, intervalCode },
          },
        })
      )
    )
  );

  broadcastIssueAttachmentUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addAttachment, IssuesActions.removeAttachment),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(IssuesSelectors.selectArchiveFilter),
        this.store.select(IssuesSelectors.selectIssuesEntitiesDict),
        this.store.select(IssuesSelectors.selectReceivedIssuesEntitiesDict),
        this.store.select(IssuesSelectors.selectSentIssuesEntitiesDict),
      ]),
      map(([action, channelId, isShortTerm, showArchived, teamIssues, receivedIssues, sentIssues]) => {
        let issue: Issue | null = null;
        if (isShortTerm && !showArchived) {
          issue = teamIssues[action.issueId] ?? receivedIssues[action.issueId] ?? sentIssues[action.issueId] ?? null;
        } else {
          issue = teamIssues[action.issueId] ?? null;
        }
        return [action, channelId, issue] as const;
      }),
      filter(([, channelId, issue]) => !!channelId && !!issue),
      map(([, channelId, issue]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: {
              _id: issue._id,
              intervalCode: issue.intervalCode,
              teamId: issue.teamId,
              attachments: issue.attachments,
            },
          },
        })
      )
    )
  );

  broadcastCommentAddedOrEdited$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addCommentSuccess, IssuesActions.editCommentSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId, comment }, channelId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.comment,
            document: { issueId, comment },
          },
        })
      )
    )
  );

  //  Broadcast effect for comment deleted
  broadcastCommentDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteCommentSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId, comment }, channelId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.commentDelete,
            document: { issueId, comment: { ...comment, text: '' } },
          },
        })
      )
    )
  );

  broadcastIssueCompleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setCompletedStateSuccess, IssuesBroadcastsActions.broadcastCompletedIssueFromMerge),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.issue,
            document: { completed: issue.completed, _id: issue._id, completedDate: issue.completedDate },
          },
        });
      })
    )
  );

  broadcastIssueDeleted$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.deleteIssueSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.delete,
            document: issueId,
          },
        });
      })
    )
  );

  broadcastIssueArchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.archiveIssueSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.archive,
            document: issueId,
          },
        });
      })
    )
  );

  broadcastIssueUnarchived$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.unarchiveIssueSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issueId }, channelId]) => {
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.unarchive,
            document: issueId,
          },
        });
      })
    )
  );

  broadcastIssueVoteChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.addVoteSuccess, IssuesActions.removeVoteSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([action, channelId]) => {
        const voteType = action.type === IssuesActions.addVoteSuccess.type ? VoteType.positive : VoteType.negative;
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.vote,
            document: {
              _id: action.issue._id,
              voteType,
              itemType: 'issue',
            },
          },
        });
      })
    )
  );

  broadcastIssueSortChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.setSort),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([_, channelId, isCurrentUserPresenter]) => !!channelId && isCurrentUserPresenter),
      map(([{ field, direction }, channelId, _, isShortTerm, teamId]) => {
        //remap direction to order
        const order =
          field === 'None'
            ? Sorted.false // 0 = reset to default
            : direction === SortDirection.ASC
              ? Sorted.ascending // 1 = ASC
              : Sorted.descending; // 2 = DESC

        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.sort,
            document: {
              listType: 'issue',
              field,
              order,
              isShortTerm,
              teamId,
            },
          },
        });
      })
    )
  );

  broadcastIssueMoved$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.moveIssueSuccess),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      map(([, channelId, isCurrentUserPresenter, isShortTerm, teamId]) => ({
        channelId,
        isCurrentUserPresenter,
        isShortTerm,
        teamId,
      })),
      filter(({ channelId, isCurrentUserPresenter }) => !!channelId && isCurrentUserPresenter),
      map(({ channelId, isShortTerm, teamId }) => {
        // Send sort reset message (field: 'None', order: Sorted.false)
        //...because on drag drop the sort is reset to default
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.sort,
            document: {
              listType: 'issue',
              field: 'None',
              order: Sorted.false,
              isShortTerm,
              teamId, //new in v2, so we don't sort if the user has another team selected
            },
          },
        });
      })
    )
  );

  broadcastTopThreeSelected$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.updateOrdinalsOnTopThreeSelectionSuccess),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(IssuesSelectors.selectIsShortTermIssues),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      map(([, channelId, isCurrentUserPresenter, isShortTerm, teamId]) => ({
        channelId,
        isCurrentUserPresenter,
        isShortTerm,
        teamId,
      })),
      filter(({ channelId, isCurrentUserPresenter }) => !!channelId && isCurrentUserPresenter),
      map(({ channelId, isShortTerm, teamId }) => {
        // Send sort reset message (field: 'None', order: Sorted.false)
        // because top3 selection resets sort to default ordinal
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.sort,
            document: {
              listType: 'issue',
              field: 'None',
              order: Sorted.false,
              isShortTerm,
              teamId,
            },
          },
        });
      })
    )
  );

  broadcastIssueSentBack$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendBackToOriginalTeamSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue }, channelId]) => {
        const { user, team, ...issueWithoutUserAndTeam } = issue;
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.sentBack,
            document: issueWithoutUserAndTeam,
          },
        });
      })
    )
  );

  broadcastIssueRecalled$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.recallIssueSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue }, channelId]) => {
        const { user, team, ...issueWithoutUserAndTeam } = issue;
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.recalled,
            document: issueWithoutUserAndTeam,
          },
        });
      })
    )
  );

  broadcastIssueSentToOtherTeam$ = createEffect(() =>
    this.actions$.pipe(
      ofType(IssuesActions.sendToTeamSuccess),
      concatLatestFrom(() => [this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting)]),
      filter(([_, channelId]) => !!channelId),
      map(([{ issue }, channelId]) => {
        // Remove user property if it exists
        const { user, team, ...issueWithoutUserAndTeam } = issue;
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.sentToOtherTeam,
            document: issueWithoutUserAndTeam, //NOTE: sending the full issue object for compatibility with issues v1
            //NEXT: after issues v1 is deprecated, improve by sending a small partial issue object
          },
        });
      })
    )
  );

  broadcastPbIssueDrop$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.updateOrdinalsOnDragDropSuccess),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([, channelId, isCurrentUserPresenter, teamId]) => !!channelId && isCurrentUserPresenter && !!teamId),
      map(([, channelId, , teamId]) =>
        RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.pbDrop,
            document: { teamId },
          },
        })
      )
    )
  );

  broadcastPbIssueSortChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RocksIssuesEvents.sortIssuesLocalAndUpdateOrdinalsSuccess),
      concatLatestFrom(() => [
        this.store.select(IssuesSelectors.selectIssueChannelIdWhenInMeeting),
        this.store.select(IssuesSelectors.selectIsCurrentUserPresenter),
        this.store.select(TeamSelectors.selectFilterBarTeamId),
      ]),
      filter(([, channelId, isCurrentUserPresenter, teamId]) => !!channelId && isCurrentUserPresenter && !!teamId),
      map(([{ sortField, sortDirection }, channelId, , teamId]) => {
        const order = sortDirection === SortDirection.ASC ? Sorted.ascending : Sorted.descending;
        return RealTimeActions.sendMessage({
          channelId,
          message: {
            messageType: IssueMessageType.pbSort,
            document: { field: sortField, order, teamId },
          },
        });
      })
    )
  );
}
